import React, { useCallback, useEffect, useState } from "react";
import { FetchingAnnouncementNotification } from "../../../Redux/Actions/NotificationActions/FetchingAnnouncement";
import "./AnnouncementCarousel.css";
import moment from "moment";
import { MdCancel } from "react-icons/md";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { AdminInterFace, Masjid } from "../../../../Types";
import { useAppThunkDispatch } from "../../../Redux/hooks";
import {
  Avatar,
  Box,
  Button,
  debounce,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import SearchDropdown from "../../AdminComponents/SearchDropdown/SearchDropdown";
import { useSearchMasjids } from "../../../graphql/Masjids/query";
import announcementIcon from "../../../Photos/Newuiphotos/nav bar/navicons/announcement.svg";
import { useGetAdminById } from "../../../graphql/Admins/query";
import toast from "react-hot-toast";

// Types
type NotificationStatus = "DEFAULT" | "EDITED" | "CANCELLED" | "EXPIRED";

interface NotificationMasjidProperty {
  _id: string;
  masjidName: string;
}

interface AnnouncementType {
  _id: string;
  title: string;
  body: string;
  type: number;
  asset: any;
  payload: any;
  isSticky: boolean;
  image: string;
  masjidId: string;
  expiresAt: string;
  createdBy: string;
  masjids: NotificationMasjidProperty[];
  createdAt: string;
  updatedAt: string;
  status: NotificationStatus;
  lastEditedAt: string;
  cancelledAt: string;
  isGlobal: boolean;
  noExpiry: boolean;
  editCount: number;
}

const ANNOUNCEMENT_TYPE_GENERAL = 0;
const ANNOUNCEMENT_TYPE_HADITH = 999;

type AnnouncementCarouselComponentProps = {
  setViewingAnnouncement: (value: boolean) => void;
  type?: number;
  label?: string;
};

const AnnouncementCard = ({
  announcement,
  onClick,
}: {
  announcement: AnnouncementType;
  onClick: (announcementId: string) => void;
}) => {
  return (
    <Card
      className="AnnouncementsCard"
      onClick={() => onClick(announcement._id)}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {announcement.title}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Triggered on:{" "}
          {moment(announcement.createdAt).format("DD MMM,YYYY hh:mm a")}
        </Typography>
        <Typography variant="body1" component="p">
          {announcement.body.length > 60
            ? `${announcement.body.slice(0, 57)}...`
            : announcement.body}
        </Typography>
      </CardContent>
    </Card>
  );
};

// Add dummy data before the component

const AnnouncementCarouselComponent = ({
  setViewingAnnouncement,
  type = ANNOUNCEMENT_TYPE_GENERAL,
  label = "Announcement",
}: AnnouncementCarouselComponentProps) => {
  const isHadith = type === ANNOUNCEMENT_TYPE_HADITH;
  const dispatch = useAppThunkDispatch();
  const [announcements, setAnnouncements] = useState<AnnouncementType[]>([]);
  const [announcementsByMasjid, setAnnouncementsByMasjid] = useState<
    AnnouncementType[]
  >([]);
  const [masjidsList, setMasjidsList] = useState<Masjid[]>([]);
  const [selectedMasjid, setSelectedMasjid] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [openAnnouncement, setOpenAnnouncement] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementType | null>(null);
  const [skipFetchAdmin, setSkipFetchAdmin] = useState(true);
  const [admin, setAdmin] = useState<AdminInterFace | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [refetch, setRefetch] = useState(false);

  const { masjids, loading, error } = useSearchMasjids(searchQuery);
  const {
    loading: loadingAdmin,
    error: errorAdmin,
    data: dataAdmin,
  } = useGetAdminById(
    selectedAnnouncement?.createdBy ?? "",
    !selectedAnnouncement?.createdBy || skipFetchAdmin
  );

  // Update masjidList when data is fetched
  useEffect(() => {
    if (masjids && !loading && !error) {
      setMasjidsList(masjids);
    }
  }, [masjids, loading, error]);

  // Fetch announcements using the GraphQL query
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await dispatch(FetchingAnnouncementNotification(0, 100));
        if (isMounted && result.success) {
          // setIsEditing(false);
          setSelectedAnnouncement(null);
          setSelectedMasjid("");
          setSearchQuery("");
          // Filter announcements by type
          const filteredByType = result.data.filter(
            (item: AnnouncementType) => item.type === type
          );
          setAnnouncements(filteredByType);
          setAnnouncementsByMasjid(filteredByType);
        }
      } catch (error) {
        if (isMounted) {
          toast.error(`Error fetching ${label.toLowerCase()}s`);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [refetch, dispatch, type, label]);

  const refetchAnnoucements = useCallback(() => {
    setRefetch((prev) => !prev);
  }, []);

  // Handle admin data
  useEffect(() => {
    if (dataAdmin && !loadingAdmin) {
      setAdmin(dataAdmin);
      setSkipFetchAdmin(true);
    } else if (errorAdmin && !loadingAdmin) {
      setAdmin(null);

      toast.error("Failed to Fetch Admin Name");
      setSkipFetchAdmin(true);
    }
  }, [dataAdmin, loadingAdmin, errorAdmin]);

  const handleGetNotifications = useCallback(
    (id: string) => {
      if (!id) {
        setAnnouncementsByMasjid(announcements);
        return;
      }
      const filteredAnnouncements = announcements.filter((item) =>
        item.masjids.some((masjid) => masjid._id === id)
      );
      setAnnouncementsByMasjid(filteredAnnouncements);
    },
    [announcements]
  );

  const onClickCard = useCallback(
    (announcementId: string) => {
      const announcement = announcements.find(
        (item) => item._id === announcementId
      );
      if (announcement) {
        setOpenAnnouncement(true);
        setSelectedAnnouncement(announcement);
      }
    },
    [announcements]
  );

  useEffect(() => {
    if (openAnnouncement && selectedAnnouncement) {
      setSkipFetchAdmin(false);
    }
  }, [openAnnouncement, selectedAnnouncement]);

  const debouncedSearchMasjids = useCallback(
    debounce((query: string) => {
      setSearchQuery(query);
    }, 300),
    []
  );

  const handleSearchMasjid = useCallback(
    (query: string) => {
      debouncedSearchMasjids(query);
    },
    [debouncedSearchMasjids]
  );

  return (
    <>
      {selectedAnnouncement && (
        <Dialog
          open={openAnnouncement && !!selectedAnnouncement}
          onClose={() => {
            setOpenAnnouncement(false);
            setSelectedAnnouncement(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{selectedAnnouncement.title}</DialogTitle>
          <DialogContent>
            <Avatar
              alt={selectedAnnouncement.title}
              src={selectedAnnouncement.image || announcementIcon}
              variant="square"
              sx={{
                width: "100%",
                height: "150px",
                marginBottom: "1rem",
                objectFit: "contain",
                "& .MuiAvatar-img": {
                  width: !selectedAnnouncement?.image ? "100px" : "100%",
                  height: !selectedAnnouncement?.image ? "auto" : "100%",
                  objectFit: "contain",
                },
              }}
            />
            <Typography variant="subtitle1" gutterBottom>
              Triggered on:{" "}
              {moment(selectedAnnouncement.createdAt).format(
                "DD MMM, YYYY hh:mm a"
              )}
            </Typography>
            {!selectedAnnouncement.noExpiry && (
              <Typography variant="subtitle1" gutterBottom>
                Expires At:{" "}
                {moment(selectedAnnouncement.expiresAt).format(
                  "DD MMM, YYYY hh:mm a"
                )}
              </Typography>
            )}
            {loadingAdmin ? (
              <Typography variant="subtitle1" gutterBottom>
                Loading admin information...
              </Typography>
            ) : (
              admin?.name && (
                <Typography variant="subtitle1" gutterBottom>
                  Triggered by: {admin.name}
                </Typography>
              )
            )}
            <Typography variant="body1" style={{ fontWeight: "600" }}>
              {`${label} Body:`}{" "}
            </Typography>
            <Box
              className="announcements-body-tx"
              style={{ marginBottom: "10px" }}
            >
              <Typography variant="body2">
                {selectedAnnouncement.body}
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom>
              Masjids:{" "}
              {selectedAnnouncement.masjids &&
              Array.isArray(selectedAnnouncement.masjids) &&
              selectedAnnouncement.masjids.length > 0
                ? selectedAnnouncement.masjids
                    .map((masjid) => masjid.masjidName)
                    .join(", ")
                : "All"}
            </Typography>
            <Button
              onClick={() => {
                setOpenAnnouncement(false);
                setSelectedAnnouncement(null);
              }}
              color="primary"
              variant="text"
              sx={{ minWidth: "0", padding: "0", margin: "0" }}
            >
              Close
            </Button>
          </DialogContent>
        </Dialog>
      )}
      <div className="AnnouncementCarouselComponent">
        <div className="AnnouncementCarouselMainContainer">
          <div className="AnnouncementTitleContainer">
            <span className="AnnouncementsTitle">
              {`Past Global ${label}s`}
            </span>
            {!isHadith && (
              <div style={{ width: "50%" }}>
                {masjidsList && (
                  <SearchDropdown
                    options={masjidsList}
                    placeholder="Type To Search Masjid."
                    onInputChange={(query: string) => {
                      if (query === "") {
                        setSelectedMasjid("");
                      }
                      handleSearchMasjid(query);
                    }}
                    onSelect={(selectedOption: Masjid | null) => {
                      if (selectedOption) {
                        setSelectedMasjid(selectedOption._id);
                        handleGetNotifications(selectedOption._id);
                      }
                    }}
                    error={false}
                    selectedValue={selectedMasjid}
                    loading={loading}
                    clearData={() => {
                      setSelectedMasjid("");
                      handleGetNotifications("");
                    }}
                  />
                )}
              </div>
            )}
            <MdCancel
              data-testid="CancelIocnAnnouncements"
              className="CancelIocnAnnouncements"
              onClick={() => setViewingAnnouncement(false)}
            />
          </div>

          <div className="AnnouncementsCarousel">
            {isLoading ? (
              <div className="EventsContainer">
                <h5 style={{ marginLeft: "10px" }}>{`Loading ${label.toLowerCase()}s...`}</h5>
              </div>
            ) : announcementsByMasjid?.length > 0 ? (
              announcementsByMasjid.map((item: AnnouncementType) => (
                <AnnouncementCard
                  key={item._id}
                  announcement={item}
                  onClick={onClickCard}
                />
              ))
            ) : (
              <div className="EventsContainer">
                <h5 style={{ marginLeft: "10px" }}>
                  {`Sorry!! No ${label}s Found`}
                </h5>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnouncementCarouselComponent;
